import { useContext } from "react";
import { useIntl } from "react-intl";

import { useMutation } from "@tanstack/react-query";

import { Inputs } from "../../../api/inputs";
import { Plants } from "../../../api/plants";
import { CafsType } from "../../../consts/cafsType";
import { MutationKey } from "../../../consts/mutation-keys";
import { QueryKey } from "../../../consts/query-keys";
import { useApiError } from "../../../hooks/useApiError";
import { useApiSuccess } from "../../../hooks/useApiSuccess";
import { useFetchError } from "../../../hooks/useFetchError";
import { ApiError } from "../../../types/api-error";
import { ApiHookConfig } from "../../../types/api-hook-config";
import { CafsTreeNodeType } from "../../../types/cafs-tree-node-type";
import { DashboardContext } from "../DashboardContext";

type UseCafsMoveProps = ApiHookConfig & {
  cafs: CafsTreeNodeType | undefined;
  targetParentName: string;
};

export const useCafsMove = ({
  onSuccess,
  cafs,
  targetParentName,
}: UseCafsMoveProps) => {
  const { selectedDataset } = useContext(DashboardContext);
  const { formatMessage } = useIntl();

  const { handleSuccess } = useApiSuccess({
    snackbarText: formatMessage(
      {
        id: "ACTION.CAFS_MOVE_SUCCESS",
        defaultMessage:
          "Item {name} has been moved to {targetParent} successfully.",
        description: "Success message for item move",
      },
      {
        name: cafs?.name,
        targetParent: targetParentName,
      },
    ),
    invalidate: [[QueryKey.DataSetDetails]],
    onSuccessFn: onSuccess,
  });

  const { handleError } = useApiError();

  const mutationFn = ({
    cafsId,
    type,
    targetId,
  }: {
    cafsId: string | number;
    type: CafsType | undefined;
    targetId: number;
  }) => {
    return type === CafsType.Input
      ? Inputs.move({
          path: {
            datasetId: +selectedDataset?.Id!,
            inputId: +cafsId!,
            targetInputId: targetId,
          },
        })
      : Plants.move({
          path: {
            datasetId: +selectedDataset?.Id!,
            plantId: +cafsId!,
            targetPlantId: targetId,
          },
        });
  };

  const {
    mutate: moveCafs,
    isPending: moveCafsPending,
    error,
  } = useMutation({
    mutationKey: [MutationKey.MovePlant, +selectedDataset!],
    mutationFn: mutationFn,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  useFetchError(error as unknown as ApiError);

  return { moveCafs, moveCafsPending };
};
